import { useEffect, useRef } from "react";
import {
  X,
  Bell,
  AlertTriangle,
  BarChart2,
  CheckCheck,
  Trash2,
  Receipt,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  AppNotification,
  NotifCategory,
  relativeTime,
  useNotifications,
} from "../contexts/NotificationContext";

const NAVY = "#2a2420";
const ORANGE = "#607840";

// ── Category config ───────────────────────────────────────────────

const CATEGORY_META: Record<
  NotifCategory,
  { label: string; Icon: React.ElementType; iconBg: string; iconColor: string }
> = {
  invoice: {
    label: "Invoice",
    Icon: Receipt,
    iconBg: "transparent",
    iconColor: ORANGE,
  },
  error: {
    label: "Error",
    Icon: AlertTriangle,
    iconBg: "transparent",
    iconColor: "#dc2626",
  },
  report: {
    label: "Report",
    Icon: BarChart2,
    iconBg: "transparent",
    iconColor: "#3b82f6",
  },
  team: {
    label: "Team",
    Icon: Users,
    iconBg: "transparent",
    iconColor: NAVY,
  },
};

// ── Section wrapper ────────────────────────────────────────────────

function Section({
  title,
  icon: Icon,
  iconColor,
  count,
  children,
}: {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        className="flex items-center gap-2 px-4 py-2.5"
        style={{
          borderBottom: `1px solid ${NAVY}09`,
          backgroundColor: "transparent",
        }}
      >
        <Icon size={12} style={{ color: iconColor }} />
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: NAVY, opacity: 0.5 }}>
          {title}
        </span>
        {count > 0 && (
          <span
            className="text-[9px] font-bold"
            style={{ color: iconColor }}
          >
            {count} new
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// ── Single notification row ───────────────────────────────────────

function NotifItem({ notif }: { notif: AppNotification }) {
  const { markRead, dismiss } = useNotifications();
  const navigate = useNavigate();
  const meta = CATEGORY_META[notif.category];

  function handleClick() {
    markRead(notif.id);
    if (notif.category === "invoice" && notif.invoiceId) {
      navigate("/invoices");
    } else if (notif.category === "error") {
      navigate("/invoices");
    } else if (notif.category === "report") {
      navigate("/");
    } else if (notif.category === "team") {
      navigate("/team");
    }
  }

  return (
    <div
      className="group relative flex items-start gap-3 px-4 py-3.5 transition-colors cursor-pointer"
      style={{
        backgroundColor: "transparent",
        borderBottom: `1px solid ${NAVY}09`,
      }}
      onClick={handleClick}
    >
      <div
        className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ backgroundColor: meta.iconBg }}
      >
        <meta.Icon size={14} style={{ color: meta.iconColor }} />
      </div>

      <div className="flex-1 min-w-0 pr-6">
        <div className="flex items-start justify-between gap-2">
          <p
            className="text-xs font-semibold leading-snug"
            style={{ color: NAVY, opacity: notif.read ? 0.65 : 1 }}
          >
            {notif.title}
          </p>
          {!notif.read && (
            <div
              className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0"
              style={{ backgroundColor: ORANGE }}
            />
          )}
        </div>
        <p
          className="text-[11px] mt-0.5 leading-relaxed"
          style={{ color: NAVY, opacity: 0.5 }}
        >
          {notif.body}
        </p>
        <div className="flex items-center gap-2 mt-1.5">
          <span
            className="text-[10px] font-medium"
            style={{ color: meta.iconColor }}
          >
            {meta.label}
          </span>
          <span className="text-[10px]" style={{ color: NAVY, opacity: 0.3 }}>
            {relativeTime(notif.timestamp)}
          </span>
        </div>
      </div>

      <button
        className="absolute right-3 top-3 opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 flex items-center justify-center rounded"
        style={{ color: NAVY }}
        onClick={(e) => {
          e.stopPropagation();
          dismiss(notif.id);
        }}
        title="Dismiss"
      >
        <X size={11} />
      </button>
    </div>
  );
}

// ── Panel ─────────────────────────────────────────────────────────

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function NotificationPanel({ open, onClose }: NotificationPanelProps) {
  const { notifications, unreadCount, markAllRead, clearAll } = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    const t = setTimeout(() => document.addEventListener("mousedown", onPointer), 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open, onClose]);

  const invoiceNotifs = notifications.filter((n) => n.category === "invoice");
  const errorNotifs = notifications.filter((n) => n.category === "error");
  const reportNotifs = notifications.filter((n) => n.category === "report");
  const teamNotifs = notifications.filter((n) => n.category === "team");

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 z-50 flex flex-col overflow-hidden rounded-2xl"
      style={{
        width: 360,
        maxHeight: "min(520px, calc(100vh - 80px))",
        backgroundColor: "#fff",
        border: `1.5px solid ${NAVY}14`,
        boxShadow: "0 16px 40px rgba(80,55,30,0.16)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 flex-shrink-0"
        style={{ backgroundColor: "#fff", borderBottom: `1.5px solid ${NAVY}12` }}
      >
        <div className="flex items-center gap-2">
          <Bell size={15} style={{ color: NAVY, opacity: 0.6 }} />
          <span className="text-sm font-bold" style={{ color: NAVY }}>Notifications</span>
          {unreadCount > 0 && (
            <span
              className="text-[10px] font-bold"
              style={{ color: ORANGE }}
            >
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md transition-colors hover:bg-gray-100"
              style={{ color: NAVY, opacity: 0.6 }}
              title="Mark all read"
            >
              <CheckCheck size={12} />
              All read
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={clearAll}
              className="flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md transition-colors hover:bg-gray-100"
              style={{ color: NAVY, opacity: 0.45 }}
              title="Clear all"
            >
              <Trash2 size={11} />
            </button>
          )}
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors hover:bg-gray-100 ml-1"
            style={{ color: NAVY, opacity: 0.5 }}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
              style={{ backgroundColor: `${NAVY}0d` }}
            >
              <Bell size={20} style={{ color: NAVY, opacity: 0.3 }} />
            </div>
            <p className="text-sm font-semibold" style={{ color: NAVY }}>All caught up</p>
            <p className="text-xs mt-1.5 leading-relaxed" style={{ color: NAVY, opacity: 0.45 }}>
              You'll be notified when new invoices arrive, errors occur, or reports are ready.
            </p>
          </div>
        ) : (
          <div>
            {invoiceNotifs.length > 0 && (
              <Section title="Invoices" icon={Receipt} iconColor={ORANGE} count={invoiceNotifs.filter((n) => !n.read).length}>
                {invoiceNotifs.map((n) => <NotifItem key={n.id} notif={n} />)}
              </Section>
            )}
            {errorNotifs.length > 0 && (
              <Section title="Errors & Flags" icon={AlertTriangle} iconColor="#dc2626" count={errorNotifs.filter((n) => !n.read).length}>
                {errorNotifs.map((n) => <NotifItem key={n.id} notif={n} />)}
              </Section>
            )}
            {reportNotifs.length > 0 && (
              <Section title="Reports" icon={BarChart2} iconColor="#3b82f6" count={reportNotifs.filter((n) => !n.read).length}>
                {reportNotifs.map((n) => <NotifItem key={n.id} notif={n} />)}
              </Section>
            )}
            {teamNotifs.length > 0 && (
              <Section title="Team" icon={Users} iconColor={NAVY} count={teamNotifs.filter((n) => !n.read).length}>
                {teamNotifs.map((n) => <NotifItem key={n.id} notif={n} />)}
              </Section>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
